import { useMemo } from "react"

import { Skeleton } from "~/components/reusable/Skeleton"
import SuperChart, { type StackedBarChartData } from "~/components/reusable/graphs/SuperChart"
import {
  pinnedAxisMax,
  signedPct,
  signedUnits,
} from "~/components/demand-2/shared/demandBreakdownShared"
import { useSeasonSalesTrend, useSeasonTrendMode } from "~/components/demand-2/useDemandIntents"
import { type FramingNode, WHOLE_COLLECTION_NODE } from "~/utils/demandFraming"
import { sumNodeTrend } from "~/utils/demandSalesTrend"
import { modeUnits, sameSeasonTrendMode, seasonTrendCeiling } from "~/utils/demandSeasonTrendMode"
import { ChartType, LegendAlignment } from "~/constants/graphs"
import { getTailwindValue } from "~/utils/tailwind"
import { DateFormatType, formatDate } from "~/utils/datetime"

const day = (iso: string | null) =>
  iso == null ? "" : formatDate(iso, DateFormatType.FullDateShort)

/**
 * "Growth from comparable seasons" as a chart: the baseline season, the one before, and the
 * planner's answer carried forward. Aggregated-only: two season windows sit 365 days apart, so a
 * week-aligned series would drift a day across the pair.
 */
const SeasonTrendBreakdown = ({
  node = WHOLE_COLLECTION_NODE,
  unconfirmed = null,
}: {
  /** Draw one hierarchy node's two seasons rather than the route's; the node's own answer applies. */
  node?: FramingNode
  /** `unconfirmedGrowthBase`: a cut stops there, as the engine caps it. */
  unconfirmed?: number | null
}) => {
  const trend = useSeasonSalesTrend()
  const mode = useSeasonTrendMode(node)
  const whole = useSeasonTrendMode()
  const { comparable, prior } = trend
  const figures = node.groupKey === "" ? trend : sumNodeTrend(trend.nodes, node)
  const { pct } = figures
  // A node following the collection's answer shares a units figure out by the level's comparable
  // sales, as `allocateSeasonTrendNodes` does; one answering on its own adds exactly its answer.
  const measured =
    node.groupKey !== "" && sameSeasonTrendMode(mode, whole)
      ? { ...figures, levelYear1: trend.year1 }
      : figures

  // The third column is the planner's answer, not the measurement.
  const answered = modeUnits(mode, measured) ?? 0
  const projectedGrowth = unconfirmed == null ? answered : Math.max(answered, -unconfirmed)
  const projectedTotal = figures.year1 + projectedGrowth
  const appliedPct = figures.year1 > 0 ? projectedGrowth / figures.year1 : null

  const aggregated = useMemo((): StackedBarChartData[] => {
    const growthLabel =
      figures.year2 > 0 && pct != null
        ? `${signedUnits(figures.year1 - figures.year2)} (${signedPct(pct / 100)}) vs the collection before`
        : null
    const projectedLabel =
      appliedPct == null
        ? null
        : `${signedUnits(projectedGrowth)} (${signedPct(appliedPct)}) applied`
    return [
      {
        name: "Realised",
        type: "column",
        color: getTailwindValue("--color-purpleheart-500"),
        // Capped at the projection so the two segments always sum to it, even when the rate is negative.
        data: [
          Math.round(figures.year2),
          Math.round(figures.year1),
          Math.round(Math.min(figures.year1, projectedTotal)),
        ],
        pointLabels: [null, growthLabel, projectedGrowth < 0 ? projectedLabel : null],
      },
      {
        name: "Projected growth",
        type: "column",
        color: getTailwindValue("--color-violet-500"),
        data: [null, null, Math.round(Math.max(projectedGrowth, 0))],
        pointLabels: [null, null, projectedGrowth >= 0 ? projectedLabel : null],
      },
    ]
  }, [figures.year1, figures.year2, pct, projectedTotal, projectedGrowth, appliedPct])

  // Sized to the tallest column ANY answer produces, so toggling moves only the projected column.
  const yAxisMax = pinnedAxisMax(seasonTrendCeiling(measured, mode))

  if (trend.isLoading) return <Skeleton className="h-[320px] w-full" />
  if (pct == null || comparable == null || prior == null) return null

  const priorName = prior.names.join(", ")
  const comparableName = comparable.names.join(", ")

  return (
    <div className="flex flex-col gap-3">
      <SuperChart
        type={ChartType.StackedColumn}
        data={aggregated}
        xCategories={[priorName, comparableName, "This collection"]}
        height={260}
        legendAlignment={LegendAlignment.Bottom}
        yAxisMax={yAxisMax}
      />
      <p className="paragraph-sm text-fg-subtle">
        {day(prior.start)} to {day(prior.end)}, then {day(comparable.start)} to{" "}
        {day(comparable.end)}.
      </p>
    </div>
  )
}

export default SeasonTrendBreakdown
